import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { Calendar, Clock, Users, AlertCircle, CheckCircle2, Package } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import JobStatusBadge from '../../../components/production/JobStatusBadge';
import PriorityBadge from '../../../components/production/PriorityBadge';
import '../../../styles/admin/production/JobsDispatch.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobsDispatch = () => {
  const [unassignedJobs, setUnassignedJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('08:00');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs?status=to_schedule`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() })
      ]);

      setUnassignedJobs(jobsRes.data);
      setTeams(teamsRes.data.filter(t => t.status === 'active'));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleAssignJob = async () => {
    if (!selectedJob || !selectedTeam || !scheduledDate) {
      alert('Veuillez sélectionner un job, une équipe et une date');
      return;
    }

    setAssigning(true);
    try {
      const scheduledStart = `${scheduledDate}T${scheduledTime}:00.000Z`;
      
      await axios.post(
        `${API_URL}/api/jobs/${selectedJob.id}/assign`,
        {
          team_id: selectedTeam.id,
          scheduled_start: scheduledStart,
          employee_ids: selectedTeam.employee_ids || []
        },
        { headers: getAuthHeader() }
      );

      // Refresh data
      await fetchData();
      
      // Reset selection
      setSelectedJob(null);
      setSelectedTeam(null);
      setScheduledDate('');
      setScheduledTime('08:00');
      
      alert('Job assigné avec succès !');
    } catch (error) {
      console.error('Error assigning job:', error);
      alert('Erreur lors de l\'assignation');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div className="dispatch-loading">Chargement...</div>;
  }

  return (
    <div className="dispatch-container">
      <div className="dispatch-header">
        <h2>Vue Dispatch</h2>
        <p>Assignez les jobs aux équipes disponibles</p>
      </div>

      <div className="dispatch-panels">
        {/* Panel 1: Unassigned Jobs */}
        <div className="dispatch-panel jobs-panel">
          <div className="panel-header">
            <Package size={20} />
            <h3>Jobs à planifier</h3>
            <span className="badge-count">{unassignedJobs.length}</span>
          </div>
          <div className="panel-content">
            {unassignedJobs.length === 0 ? (
              <div className="empty-state">
                <CheckCircle2 size={48} />
                <p>Aucun job à planifier</p>
              </div>
            ) : (
              unassignedJobs.map((job) => (
                <div
                  key={job.id}
                  className={`job-card ${selectedJob?.id === job.id ? 'selected' : ''}`}
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="job-card-header">
                    <span className="job-number">{job.job_number}</span>
                    <PriorityBadge priority={job.priority} />
                  </div>
                  <h4 className="job-title">{job.title}</h4>
                  <p className="job-client">{job.service_address.city}</p>
                  <div className="job-meta">
                    <span className="meta-item">
                      <Clock size={14} />
                      {job.estimated.duration_minutes} min
                    </span>
                    <span className="meta-item">
                      {job.services.length} service(s)
                    </span>
                  </div>
                  {job.scheduling.target_start_date && (
                    <div className="job-target-date">
                      <Calendar size={14} />
                      Cible: {format(new Date(job.scheduling.target_start_date), 'dd MMM yyyy', { locale: fr })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Panel 2: Teams */}
        <div className="dispatch-panel teams-panel">
          <div className="panel-header">
            <Users size={20} />
            <h3>Équipes disponibles</h3>
            <span className="badge-count">{teams.length}</span>
          </div>
          <div className="panel-content">
            {teams.map((team) => (
              <div
                key={team.id}
                className={`team-card ${selectedTeam?.id === team.id ? 'selected' : ''}`}
                onClick={() => setSelectedTeam(team)}
              >
                <div className="team-header">
                  <h4>{team.name}</h4>
                  <span className="team-code">{team.code}</span>
                </div>
                <div className="team-details">
                  <div className="team-meta">
                    <Users size={14} />
                    <span>{team.employee_ids.length} membre(s)</span>
                  </div>
                  {team.home_zone && (
                    <div className="team-zone">{team.home_zone}</div>
                  )}
                </div>
                {team.service_capabilities.length > 0 && (
                  <div className="team-capabilities">
                    {team.service_capabilities.slice(0, 3).map((cap, idx) => (
                      <span key={idx} className="capability-tag">{cap}</span>
                    ))}
                    {team.service_capabilities.length > 3 && (
                      <span className="capability-more">+{team.service_capabilities.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Panel 3: Assignment Form */}
        <div className="dispatch-panel assignment-panel">
          <div className="panel-header">
            <Calendar size={20} />
            <h3>Planification</h3>
          </div>
          <div className="panel-content">
            {!selectedJob || !selectedTeam ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <p>Sélectionnez un job et une équipe</p>
              </div>
            ) : (
              <div className="assignment-form">
                <div className="assignment-summary">
                  <h4>Résumé</h4>
                  <div className="summary-item">
                    <strong>Job:</strong>
                    <span>{selectedJob.job_number} - {selectedJob.title}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Équipe:</strong>
                    <span>{selectedTeam.name}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Lieu:</strong>
                    <span>{selectedJob.service_address.city}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Durée estimée:</strong>
                    <span>{selectedJob.estimated.duration_minutes} min</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Date d'intervention *</label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="form-control"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label>Heure de début *</label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="form-control"
                  />
                </div>

                <button
                  onClick={handleAssignJob}
                  disabled={assigning || !scheduledDate}
                  className="btn-primary btn-assign"
                >
                  {assigning ? 'Assignation...' : 'Assigner le job'}
                </button>

                <button
                  onClick={() => {
                    setSelectedJob(null);
                    setSelectedTeam(null);
                    setScheduledDate('');
                  }}
                  className="btn-secondary"
                  disabled={assigning}
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsDispatch;